import StrategyAgent from './StrategyAgent.js';
import SignalAgent from './SignalAgent.js';
import VotingCoordinator from './VotingCoordinator.js';

// Simplified logger for demo purposes
const elizaLogger = {
    info: (message, data) => console.log('INFO:', message, data || ''),
    warn: (message, data) => console.warn('WARN:', message, data || ''),
    error: (message, data) => console.error('ERROR:', message, data || '')
};

/**
 * CrossfluxxAgentSystem - Main coordinator for all agents
 * 
 * This system manages the lifecycle and interaction between:
 * - StrategyAgent: Backtests and optimizes yield strategies
 * - SignalAgent: Monitors market data and APR feeds
 * - VotingCoordinator: Makes consensus-based rebalancing decisions
 */
class CrossfluxxAgentSystem {
    constructor(config) {
        this.config = {
            // RPC endpoints for each chain
            ethereumRpc: config.ethereumRpc || 'https://ethereum.publicnode.com',
            arbitrumRpc: config.arbitrumRpc || 'https://arbitrum.publicnode.com',
            polygonRpc: config.polygonRpc || 'https://polygon.publicnode.com',
            
            // API keys for data sources
            coingeckoApiKey: config.coingeckoApiKey,
            defiLlamaApiKey: config.defiLlamaApiKey,
            
            // System parameters
            rebalanceInterval: config.rebalanceInterval || 24 * 60 * 60 * 1000, // 24 hours
            minimumConfidence: config.minimumConfidence || 0.6,
            consensusThreshold: config.consensusThreshold || 0.7,
            
            // Risk management
            maxRiskTolerance: config.maxRiskTolerance || 0.5,
            emergencyStopEnabled: config.emergencyStopEnabled !== false,
            
            ...config
        };

        this.agents = {
            strategy: null,
            signal: null,
            coordinator: null
        };

        this.isInitialized = false;
        this.isRunning = false;
        this.lastRebalanceCheck = null;
        this.systemMetrics = {
            uptime: 0,
            totalDecisions: 0,
            successfulRebalances: 0,
            errors: 0
        };
    }

    async initialize() {
        try {
            elizaLogger.info("Initializing Crossfluxx Agent System...");

            // Initialize individual agents
            await this.initializeAgents();
            
            // Set up inter-agent communication
            await this.setupAgentCommunication();
            
            // Start monitoring loops (simplified for demo)
            this.startMonitoringLoops();
            
            this.isInitialized = true;
            this.isRunning = true;
            this.systemMetrics.uptime = Date.now();
            
            elizaLogger.info("Crossfluxx Agent System initialized successfully");
            return true;
            
        } catch (error) {
            elizaLogger.error("Failed to initialize agent system:", error);
            this.isInitialized = false;
            this.isRunning = false;
            throw error;
        }
    }

    async initializeAgents() {
        elizaLogger.info("Initializing individual agents...");

        try {
            // Initialize agents in parallel to speed up the process
            this.agents.strategy = new StrategyAgent(this.config);
            this.agents.signal = new SignalAgent(this.config);
            this.agents.coordinator = new VotingCoordinator(this.config);

            // Initialize all agents in parallel
            await Promise.all([
                this.agents.strategy.initialize().catch(e => elizaLogger.error("StrategyAgent init failed:", e)),
                this.agents.signal.initialize().catch(e => elizaLogger.error("SignalAgent init failed:", e)),
                this.agents.coordinator.initialize().catch(e => elizaLogger.error("VotingCoordinator init failed:", e))
            ]);

            elizaLogger.info("All agents initialized");
        } catch (error) {
            elizaLogger.error("Agent initialization error:", error);
            // Continue anyway to see what works
        }
    }

    async setupAgentCommunication() {
        elizaLogger.info("Setting up inter-agent communication channels...");
        
        // Set up event listeners for agent communication
        this.setupStrategySignalLink();
        this.setupSignalCoordinatorLink();
        this.setupCoordinatorFeedback();
    }

    setupStrategySignalLink() {
        // Strategy agent can request market data from signal agent
        if (this.agents.strategy && this.agents.signal) {
            // In a real implementation, this would set up proper event channels
            elizaLogger.info("Strategy-Signal communication link established");
        }
    }

    setupSignalCoordinatorLink() {
        // Signal agent feeds market data to coordinator
        if (this.agents.signal && this.agents.coordinator) {
            elizaLogger.info("Signal-Coordinator communication link established");
        }
    }

    setupCoordinatorFeedback() {
        // Coordinator can request strategy updates and signal analysis
        if (this.agents.coordinator) {
            elizaLogger.info("Coordinator feedback loops established");
        }
    }

    startMonitoringLoops() {
        elizaLogger.info("Starting system monitoring loops...");
        
        // Only start monitoring if agents are properly initialized
        if (this.agents.strategy && this.agents.signal && this.agents.coordinator) {
            // Start periodic system health checks (longer interval to reduce overhead)
            this.healthCheckInterval = setInterval(() => {
                this.performHealthCheck();
            }, 300000); // Every 5 minutes instead of 1 minute
            
            // Start rebalance evaluation checks (much longer interval)
            this.rebalanceCheckInterval = setInterval(() => {
                this.checkRebalanceTriggers();
            }, Math.max(this.config.rebalanceInterval, 3600000)); // At least 1 hour
            
            elizaLogger.info("Monitoring loops started successfully");
        } else {
            elizaLogger.warn("Skipping monitoring loops - agents not fully initialized");
        }
    }

    performHealthCheck() {
        try {
            // Check agent status
            const agentStatuses = Object.keys(this.agents).map(name => ({
                name,
                status: this.agents[name] ? 'healthy' : 'error'
            }));
            
            // Update metrics
            this.systemMetrics.uptime = Date.now() - this.systemMetrics.uptime;
            
            elizaLogger.info("Health check completed", { agentStatuses });
        } catch (error) {
            elizaLogger.error("Health check failed:", error);
        }
    }

    async checkRebalanceTriggers() {
        try {
            // Simplified trigger checking
            const shouldRebalance = await this.evaluateRebalanceNeed();
            
            if (shouldRebalance) {
                elizaLogger.info("Rebalance trigger detected");
                // Would trigger actual rebalance in production
            }
        } catch (error) {
            elizaLogger.error("Rebalance check failed:", error);
        }
    }

    async evaluateRebalanceNeed() {
        // TODO: Implement real rebalancing need evaluation
        console.log("❌ evaluateRebalanceNeed not implemented - needs real market data analysis");
        return false;
    }

    // Public API methods for frontend integration
    async getSystemStatus() {
        return {
            isInitialized: this.isInitialized,
            isRunning: this.isRunning,
            metrics: this.systemMetrics,
            lastRebalanceCheck: this.lastRebalanceCheck,
            agentStatus: Object.keys(this.agents).reduce((status, name) => {
                status[name] = this.agents[name] ? 'running' : 'not_initialized';
                return status;
            }, {})
        };
    }

    async getCurrentMarketData() {
        if (!this.agents.signal) {
            throw new Error("Signal agent not available");
        }
        
        return this.agents.signal.getCurrentMarketSnapshot();
    }

    async forceRebalanceEvaluation() {
        // TODO: Implement real manual rebalance evaluation
        console.log("❌ forceRebalanceEvaluation not implemented - needs real agent coordination");
        return {
            action: 'no_action',
            confidence: 0,
            consensus: 0,
            timestamp: Date.now(),
            reasoning: ['Function not implemented'],
            executionPlan: null,
            error: "Manual evaluation not implemented"
        };
    }

    async shutdown() {
        try {
            elizaLogger.info("Shutting down Crossfluxx Agent System...");
            
            // Clear monitoring intervals
            if (this.healthCheckInterval) {
                clearInterval(this.healthCheckInterval);
                this.healthCheckInterval = null;
            }
            
            if (this.rebalanceCheckInterval) {
                clearInterval(this.rebalanceCheckInterval);
                this.rebalanceCheckInterval = null;
            }
            
            // Shutdown individual agents if they have shutdown methods
            for (const [name, agent] of Object.entries(this.agents)) {
                if (agent && typeof agent.shutdown === 'function') {
                    try {
                        await agent.shutdown();
                        elizaLogger.info(`${name} agent shutdown complete`);
                    } catch (error) {
                        elizaLogger.error(`Error shutting down ${name} agent:`, error);
                    }
                }
            }
            
            this.isRunning = false;
            this.isInitialized = false;
            
            elizaLogger.info("Crossfluxx Agent System shutdown complete");
            return true;
            
        } catch (error) {
            elizaLogger.error("Error during agent system shutdown:", error);
            return false;
        }
    }
}

// Factory function to create and initialize the agent system
export async function createCrossfluxxAgentSystem(config = {}) {
    const system = new CrossfluxxAgentSystem(config);
    await system.initialize();
    return system;
}

// Default export
export default CrossfluxxAgentSystem; 